import { SegmentStrategy } from "../rules/intoSegments";
import { PhoneLengths } from "../rules/lengthRules";
import { RuleSet } from "../rules/ruleSet";
import { RulesRunner } from "../rules/rulesRunner";
import { wordsToIpa } from "../transcribe/wordsToIpa";
import { dictionaryUS, englishAccentRules, EnglishAccentType } from "./accentRules";
import { americanistToIpa } from "./americanist";
import { americanizedSpanish } from "./americanize";
import { cxsToIpa } from "./cxs";
import { englishToIpa } from "./englishToIpa";
import { lushootseedToIpa } from "./lushootseed";
import { oldEnglishRules } from "./oldEnglish";
import { sanskritTransliteration } from "./sanskrit";
import { mexicanAccent, spanishAccent, SpanishAccentType, spanishToIpa } from "./spanishToIpa";

export type SupportedLanguage =
  | 'English' | 'Old English' | 'Spanish' | 'Lushootseed'
  | 'IPA' | 'Americanist' | 'Sanskrit (IAST)' | 'CXS'
  ;
export const supportedLanguageItems = [
  'English', 'Old English', 'Spanish', 'Lushootseed',
  'IPA', 'Americanist', 'Sanskrit (IAST)', 'CXS',
];

export type SupportedAccent = EnglishAccentType | SpanishAccentType | 'none';

/** knows how to convert text to IPA using various languages & accents */
export class AllRules {
  /** get singleton instance */
  static get(): AllRules {
    if (this.all === null) {
      this.all = new AllRules();
    }
    return this.all;
  }
  private static all: AllRules | null = null;

  /** map from language/accent to rules */
  private nameToRunner: Record<string, RulesRunner> = {};
  /** try to pronounce without a dictionary */
  private usEnglishRules = new RulesRunner(englishToIpa);

  private constructor() {
    const ipaRule: RuleSet = { inputSegments: SegmentStrategy.IpaPlus, preprocess: ['cleanPunctuation', 'normIpa'] };
    this.add('IPA', ipaRule);
    // languages
    this.add('Old English', oldEnglishRules);
    this.add('Lushootseed', lushootseedToIpa);
    // transliterations
    this.add('Americanist', americanistToIpa);
    this.add('Sanskrit (IAST)', sanskritTransliteration);
    this.add('CXS', cxsToIpa);
    // English
    this.add('English', dictionaryUS);  // default if no accent specified
    const accentNames = Object.keys(englishAccentRules);
    accentNames.forEach(name => this.add(name, englishAccentRules[name]));
    // Spanish
    this.add('Spanish', spanishToIpa);  // default if no accent specified
    this.add("Spain", spanishAccent);
    this.add("Mexico", mexicanAccent);
    this.add("Americanized", americanizedSpanish);
  }

  /** convert input to the given language & optional accent */
  convert(
    input: string, language: SupportedLanguage, accent?: SupportedAccent
  ): { afterRules: string, badInput?: string[] } {
    const runner = this.getRules(language, accent);
    if (!runner) {
      return { afterRules: input };
    }

    if (language === 'English') {
      const [, notfound] = wordsToIpa(input, 'en_US', this.fallback);
      const badInput = AllRules.deDupe(notfound);
      const [afterRules,] = runner.runRulesOnRaw(input, this.fallback);
      return { afterRules, badInput };
    } else if (language === 'Spanish' && accent !== undefined) {
      const runner1 = this.getRules(language);
      const result1 = runner1.runRulesOnIpa(input);
      const result2 = runner.runRulesOnIpa(result1.ipa);
      return { afterRules: result2.ipa };
    } else {
      const result = runner.runRulesOnIpa(input);
      const badInput = !result.hasUnknowns ? [] : result.hasUnknowns.map(p => p.phoneme);
      return { afterRules: result.ipa, badInput };
    }
  }

  /** get the rule for adding phoneme length */
  getAddPhonemeLength(
    language: SupportedLanguage, accent?: SupportedAccent
  ): PhoneLengths | undefined {
    const runner = this.getRules(language, accent);
    return runner ? runner.getAddPhonemeLength() : undefined;
  }

  /** remove duplicate strings */
  static deDupe(list: string[]): string[] {
    const result: string[] = []; // de-dupe
    new Set(list).forEach(i => result.push(i));
    return result;
  }

  private getRules(language: SupportedLanguage, accent?: SupportedAccent): RulesRunner {
    if (accent) {
      const accentRunner = this.nameToRunner[accent];
      if (accentRunner) {
        return accentRunner;
      }
    }
    return this.nameToRunner[language];
  }

  /** if an English word isn't in the dictionary, use these rules to guess at a pronunciation */
  private fallback = (word: string): string => {
    const result = this.usEnglishRules.runRulesOnIpa(word);
    return result.ipa;
  }

  /** attach rules to a name */
  private add(name: string, rules: RuleSet) {
    this.nameToRunner[name] = new RulesRunner(rules);
  }
}
